/**
 * Stories — the episodes the classifier has grouped signals into.
 *
 * A story is one real-world development reported more than once: same actors,
 * same underlying occurrence, revealed in instalments and covered by different
 * outlets. Pass 2 decides which one a signal belongs to, or opens a new one.
 *
 * **Read-only, and that is a decision rather than a stage.** Renaming a story,
 * merging two that turned out to be one, and splitting one that turned out to
 * be two are all real needs — and all of them are judgements about what the
 * corpus means, not fixes to a display. They deserve a review flow of the kind
 * watch-area proposals have, which is a later phase's work.
 *
 * What this page is for now is **looking at the grouping**. A story assignment
 * nobody can see is a column that could be wrong in a way nothing would reveal,
 * and the first thing anybody will want to know is whether two articles about
 * one incident actually landed together.
 */
import { api, ApiError } from "../lib/api";
import { renderHeader } from "../components/header";
import type { RouteParams } from "../lib/router";

interface StoryRecord {
  id: string;
  title: string;
  firstSeenAt: string;
  lastSeenAt: string;
  signalCount: number;
  /** What the signals table actually says. Shown beside the stored counter
   *  when they disagree — novelty scoring reads the counter, so a drift is a
   *  wrong score rather than a cosmetic mismatch. */
  actualSignalCount: number | null;
  rollingSummary: string | null;
  summaryUpdatedAt: string | null;
  createdByVersion: number | null;
}

interface StorySignal {
  id: string;
  signalRef: string | null;
  title: string;
  source: string;
  datePublished: string;
  eventDate: string | null;
  signalType: string | null;
  claimStatus: string | null;
  horizon: string | null;
  noveltyScore: number | null;
}

function esc(s: string): string {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
}

/** `2026-09-14T08:00:00Z` → `2026-09-14`. The time of day tells a reader
 *  nothing about an episode that has been running for weeks. */
function day(iso: string): string {
  return (iso || "").slice(0, 10);
}

function failure(page: HTMLElement, what: string, err: unknown): void {
  const missing = err instanceof ApiError && err.status === 404;
  page.innerHTML = missing
    ? `<h2>No such story</h2>
       <p class="muted">Nothing here answers to that id. It may have been merged away.</p>`
    : `<h2>Could not load ${esc(what)}</h2>
       <p class="muted">${esc(err instanceof ApiError ? err.message : "Unknown error")}</p>`;
}

// ── The list ────────────────────────────────────────────────────────────────

export async function renderStoriesPage(root: HTMLElement): Promise<void> {
  root.innerHTML = "";
  root.appendChild(renderHeader());
  const page = document.createElement("div");
  page.className = "sty-page";
  page.innerHTML = `<p class="muted">Loading stories…</p>`;
  root.appendChild(page);

  let data: { stories: StoryRecord[]; total: number; capped: boolean };
  try {
    data = await api("/stories");
  } catch (err) {
    failure(page, "the story list", err);
    return;
  }

  if (!data.stories.length) {
    page.innerHTML = `
      <h2>Stories</h2>
      <p class="muted">
        Nothing yet. Stories are opened by the analysis pass as it classifies new
        signals, so the first ones appear after the next scan. Signals collected
        before v6 carry no story and are not backfilled here.
      </p>`;
    return;
  }

  const rows = data.stories
    .map((s) => {
      // The two counts should agree. When they do not, the stored one is what
      // novelty scoring read, so the disagreement is worth a reader's eye
      // rather than a silent correction on display.
      const drift =
        s.actualSignalCount !== null && s.actualSignalCount !== s.signalCount
          ? ` <span class="sty-drift" title="The stored counter and the signals table disagree">counter says ${s.signalCount}</span>`
          : "";
      const count = s.actualSignalCount ?? s.signalCount;
      return `<tr>
        <td><a href="/stories/${encodeURIComponent(s.id)}" data-route>${esc(s.title)}</a></td>
        <td class="sty-num">${count}${drift}</td>
        <td>${esc(day(s.firstSeenAt))}</td>
        <td>${esc(day(s.lastSeenAt))}</td>
      </tr>`;
    })
    .join("");

  page.innerHTML = `
    <h2>Stories</h2>
    <p class="muted">
      One real-world episode each, with the signals reporting it. Grouped by the
      analysis pass; two developments that merely share a topic are two stories.
    </p>
    ${data.capped
      ? `<p class="muted">Showing the ${data.stories.length} most recently
         active of ${data.total}.</p>`
      : ""}
    <div class="ap-table-wrap"><table class="ap-table sty-table">
      <thead><tr><th>Story</th><th>Signals</th><th>First seen</th><th>Last seen</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
}

// ── One story ───────────────────────────────────────────────────────────────

export async function renderStoryPage(
  root: HTMLElement,
  params: RouteParams,
): Promise<void> {
  root.innerHTML = "";
  root.appendChild(renderHeader());
  const page = document.createElement("div");
  page.className = "sty-page";
  page.innerHTML = `<p class="muted">Loading story…</p>`;
  root.appendChild(page);

  let data: { story: StoryRecord; signals: StorySignal[] };
  try {
    data = await api(`/stories/${encodeURIComponent(params["id"] ?? "")}`);
  } catch (err) {
    failure(page, "that story", err);
    return;
  }

  const { story, signals } = data;
  const rows = signals
    .map((s) => {
      const ref = s.signalRef
        ? `<a href="/signals/${encodeURIComponent(s.signalRef)}" data-route>${esc(s.signalRef)}</a>`
        : `<span class="muted">—</span>`;
      const novelty =
        s.noveltyScore !== null && s.noveltyScore !== undefined
          ? s.noveltyScore.toFixed(2)
          : `<span class="muted">—</span>`;
      return `<tr>
        <td>${esc(s.datePublished)}</td>
        <td>${esc(s.title)}<br /><span class="muted">${esc(s.source)}</span></td>
        <td>${s.signalType ? esc(s.signalType) : `<span class="muted">—</span>`}</td>
        <td>${s.claimStatus ? esc(s.claimStatus) : `<span class="muted">—</span>`}</td>
        <td>${s.horizon ? esc(s.horizon) : `<span class="muted">—</span>`}</td>
        <td class="sty-num">${novelty}</td>
        <td>${ref}</td>
      </tr>`;
    })
    .join("");

  page.innerHTML = `
    <p class="muted"><a href="/stories" data-route>← Stories</a></p>
    <h2 class="sty-title">${esc(story.title)}</h2>
    <p class="muted sty-meta">
      ${signals.length} signal${signals.length === 1 ? "" : "s"} ·
      first seen ${esc(day(story.firstSeenAt))} ·
      last seen ${esc(day(story.lastSeenAt))}
    </p>
    ${story.rollingSummary
      ? `<div class="sty-summary">${esc(story.rollingSummary)}</div>
         <p class="muted">Summarised ${esc(day(story.summaryUpdatedAt ?? ""))}.</p>`
      : `<p class="muted">No rolling summary yet — those are written by a later
         pass, between briefs.</p>`}
    <div class="ap-table-wrap"><table class="ap-table sty-table">
      <thead><tr>
        <th>Published</th><th>Signal</th><th>Type</th><th>Claim</th>
        <th>Horizon</th><th>Novelty</th><th>Ref</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
}
